/**
 * The pages whose `page.create` has not reached the server yet, as tree rows.
 *
 * `060-notebook-tree.md` §6 is unambiguous about what must happen to them: a create that has
 * not reached the server *"stays in the tree, rendered with a subtle pending dot. It is fully
 * editable; the edits queue behind it."* **Stays.** Not "usually stays".
 *
 * It did not stay. `useCreatePage` writes the new row into the tree cache optimistically, and
 * that write is the only copy: the next refetch of `GET /api/pages/tree` — invalidated by the
 * previous save, by a window focus, by anything — replaces the cache wholesale, and if the
 * `POST` is still queued behind it in the outbox, the server's answer does not contain the new
 * page. The row vanishes. It comes back a moment later when the create lands.
 *
 * In that moment the row's inline title input is UNMOUNTED, and it is an uncontrolled input, so
 * **whatever the user had typed into it is gone**. They named a page, watched it commit itself
 * as "Untitled", and had no idea why. It reproduced roughly one run in three under a seeded
 * notebook, which is exactly the frequency that gets a test retried instead of a bug fixed.
 *
 * So the tree is rendered from the union of what the server knows and what the outbox is still
 * holding. The outbox is durable and it is the source of truth for "this page exists"; the
 * server merely has not been told yet.
 */
import { useEffect, useState } from 'react';
import type { TreePage } from '@/api/endpoints/pages';
import { listEntries, subscribe } from '@/offline/outbox';

const EMPTY: readonly TreePage[] = [];

interface CreateBody {
  id: string;
  parentId: string | null;
  position: string;
  title?: string;
}

function isCreateBody(body: unknown): body is CreateBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    'id' in body &&
    'position' in body &&
    typeof (body as { position: unknown }).position === 'string'
  );
}

/** Referential stability: the tree must not re-render 500 rows because nothing changed. */
function same(a: readonly TreePage[], b: readonly TreePage[]): boolean {
  return (
    a.length === b.length &&
    a.every((page, i) => {
      const other = b[i];
      return (
        other !== undefined &&
        page.id === other.id &&
        page.title === other.title &&
        page.parentId === other.parentId &&
        page.position === other.position
      );
    })
  );
}

export function usePendingPages(): readonly TreePage[] {
  const [pages, setPages] = useState<readonly TreePage[]>(EMPTY);

  useEffect(() => {
    let live = true;

    const read = (): void => {
      void listEntries().then((entries) => {
        if (!live) return;

        const rows: TreePage[] = [];
        for (const entry of entries) {
          if (entry.kind !== 'page.create') continue;

          const body = entry.request.body;
          if (!isCreateBody(body)) continue;

          rows.push({
            id: body.id,
            parentId: body.parentId,
            position: body.position,
            // The label is kept in step with the title by the outbox's coalescing; the body is
            // the create's own, which is "Untitled" until the rename lands. Either way the row
            // exists, and that is the point.
            title: body.title ?? entry.label ?? 'Untitled',
            icon: null,
            isFavorite: false,
            hasChildren: false,
            dueAt: null,
            updatedAt: new Date(entry.createdAt).toISOString(),
          });
        }

        setPages((previous) => (same(previous, rows) ? previous : rows));
      });
    };

    read();
    const unsubscribe = subscribe(read);

    return () => {
      live = false;
      unsubscribe();
    };
  }, []);

  return pages;
}
